// ============================================
// Sync Status Bar Component
// ============================================

import { RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import type { SyncStatus } from '../../types/api';
import { formatRelativeTime } from '../../utils/formatters';

interface SyncStatusBarProps {
  sources: SyncStatus[];
}

const sourceLabels: Record<string, string> = {
  facebook_ads: 'Facebook Ads',
  google_ads: 'Google Ads',
  ga4: 'Google Analytics',
  monday: 'Monday.com',
};

function StatusIcon({ status }: { status: string | null }) {
  switch (status) {
    case 'success':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'running':
      return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
    default:
      return <Clock className="w-4 h-4 text-gray-400" />;
  }
}

function getStatusColor(status: string | null): string {
  switch (status) {
    case 'success':
      return 'bg-green-50 border-green-200';
    case 'failed':
      return 'bg-red-50 border-red-200';
    case 'running':
      return 'bg-blue-50 border-blue-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
}

export function SyncStatusBar({ sources }: SyncStatusBarProps) {
  if (!sources || sources.length === 0) {
    return null;
  }

  const allSuccess = sources.every((s) => s.lastSyncStatus === 'success');
  const anyFailed = sources.some((s) => s.lastSyncStatus === 'failed');
  const anyRunning = sources.some((s) => s.lastSyncStatus === 'running');

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Trạng thái đồng bộ dữ liệu</h3>
        <div className="flex items-center gap-2">
          {anyRunning && (
            <span className="flex items-center gap-1 text-xs text-blue-600">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Đang đồng bộ
            </span>
          )}
          {allSuccess && !anyRunning && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              Đã cập nhật
            </span>
          )}
          {anyFailed && (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <XCircle className="w-3 h-3" />
              Có lỗi
            </span>
          )}
        </div>
      </div>

      {/* Source Status Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {sources.map((source) => (
          <div
            key={source.sourceName}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border ${getStatusColor(source.lastSyncStatus)}`}
          >
            <StatusIcon status={source.lastSyncStatus} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-700 truncate">
                {sourceLabels[source.sourceName] || source.sourceName}
              </p>
              <p className="text-xs text-gray-500">
                {source.lastSyncAt
                  ? formatRelativeTime(source.lastSyncAt)
                  : 'Chưa đồng bộ'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Error Message (if any) */}
      {anyFailed && (
        <div className="mt-3 p-2 bg-red-50 rounded-md">
          {sources
            .filter((s) => s.lastSyncStatus === 'failed' && s.errorMessage)
            .map((s) => (
              <p key={s.sourceName} className="text-xs text-red-600">
                <span className="font-medium">{sourceLabels[s.sourceName]}:</span>{' '}
                {s.errorMessage}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
